import type { HeroId } from '../types';
import type { ShopResult } from './economy';
import { pushLedger } from './ledger';
import { RIBBON_HONEY } from './tables';
import type { CosmeticState, Profile } from './types';

/** Repeatable acorn hangings, bought only after the finite shelf is clear. */
export const GARLAND_ACORNS = 1800;
/** Repeatable honey lights, bought only after the honey shelf is clear. */
export const LANTERN_HONEY = 30;

export type CosmeticKind = 'outfit' | 'board' | 'hub' | 'prestige' | 'keepsake';

export interface CosmeticDef {
  id: string;
  name: string;
  blurb: string;
  kind: CosmeticKind;
  acorns: number;
  honey: number;
  hero?: HeroId;
  file: string;
}

/**
 * Worn pieces only. Glen decor, seals, and duel tables were retired: they are
 * not sold, and `migrateShelfDecor` drops them from older saves.
 */
export const COSMETICS: readonly CosmeticDef[] = [
  { id: 'pip-scarf', name: 'Travel scarf', blurb: 'Pip’s knitted acorn scarf', kind: 'outfit', acorns: 700, honey: 0, hero: 'pip', file: 'cc_outfit_pip_scarf.jpg' },
  { id: 'bramble-vest', name: 'Travel vest', blurb: 'Bramble’s moss vest', kind: 'outfit', acorns: 1600, honey: 0, hero: 'bramble', file: 'cc_outfit_bramble_vest.jpg' },
  { id: 'cleo-cloak', name: 'Dusk cloak', blurb: 'Cleo’s berry cloak', kind: 'outfit', acorns: 2200, honey: 0, hero: 'cleo', file: 'cc_outfit_cleo_cloak.jpg' },
  { id: 'otto-sash', name: 'Tide sash', blurb: 'Otto’s moonstone sash', kind: 'outfit', acorns: 2400, honey: 0, hero: 'otto', file: 'cc_outfit_otto_sash.jpg' },
  { id: 'honey-crown', name: 'Honey crown', blurb: 'A leaf crown, honey only', kind: 'keepsake', acorns: 0, honey: 30, file: 'cc_honey_crown.jpg' },
  { id: 'honey-cloak', name: 'Honey cloak', blurb: 'A folded dusk cloak', kind: 'keepsake', acorns: 0, honey: 50, file: 'cc_honey_cloak.jpg' },
  { id: 'honey-locket', name: 'Glen locket', blurb: 'A thistle in gold', kind: 'keepsake', acorns: 0, honey: 60, file: 'cc_honey_locket.jpg' },
];

/**
 * Glen decor a critter does not wear. Wiped from owned and placed data.
 * Garland and lantern counts stay; they are repeatable shelf buys.
 */
export const RETIRED_COSMETIC_IDS: readonly string[] = [
  'porch-lantern',
  'window-flowers',
  'glen-banner',
  'seal-pip',
  'seal-bramble',
  'seal-cleo',
  'seal-otto',
  'moss-table',
  'moon-table',
  'honey-jar',
];

const RETIRED = new Set(RETIRED_COSMETIC_IDS);

export function retiredCosmetic(id: string): boolean {
  return RETIRED.has(id);
}

export const GARLAND_FILE = 'cc_garland.jpg';
export const LANTERN_FILE = 'cc_honey_lantern.jpg';

export function cosmeticKey(file: string): string {
  return `cosmetic-${file.replace(/\.[a-z]+$/i, '')}`;
}

export function emptyCosmetics(): CosmeticState {
  return {
    ribbon: false,
    owned: [],
    equipped: { outfit: {}, board: null, hub: [] },
    garlands: 0,
    lanterns: 0,
  };
}

/** Old glens stored only the ribbon. Fill the shelf fields without touching the wallet. */
export function normalizeCosmetics(profile: Profile): void {
  const raw = profile.cosmetics as CosmeticState & {
    owned?: unknown;
    equipped?: Partial<CosmeticState['equipped']> | null;
    garlands?: unknown;
    lanterns?: unknown;
  };
  if (!Array.isArray(raw.owned)) raw.owned = [];
  const equipped = raw.equipped;
  if (!equipped || typeof equipped !== 'object') {
    raw.equipped = { outfit: {}, board: null, hub: [] };
  } else {
    if (!equipped.outfit || typeof equipped.outfit !== 'object' || Array.isArray(equipped.outfit)) equipped.outfit = {};
    if (typeof equipped.board !== 'string') equipped.board = null;
    if (!Array.isArray(equipped.hub)) equipped.hub = [];
  }
  if (typeof raw.garlands !== 'number' || !Number.isInteger(raw.garlands) || raw.garlands < 0) raw.garlands = 0;
  if (typeof raw.lanterns !== 'number' || !Number.isInteger(raw.lanterns) || raw.lanterns < 0) raw.lanterns = 0;
  profile.cosmetics = raw;
}

export function cosmeticById(id: string): CosmeticDef | undefined {
  return COSMETICS.find((item) => item.id === id);
}

export function cosmeticOpen(profile: Profile, item: CosmeticDef): boolean {
  if (!item.hero) return true;
  return profile.critters[item.hero]?.unlocked === true;
}

export function ownsCosmetic(profile: Profile, id: string): boolean {
  return profile.cosmetics.owned.includes(id);
}

/** Cheapest acorn piece the player can see. Null once that shelf is clear. */
export function cheapestAcornCosmetic(profile: Profile): CosmeticDef | null {
  let best: CosmeticDef | null = null;
  for (const item of COSMETICS) {
    if (item.acorns <= 0 || ownsCosmetic(profile, item.id) || !cosmeticOpen(profile, item)) continue;
    if (!best || item.acorns < best.acorns || (item.acorns === best.acorns && item.id < best.id)) best = item;
  }
  return best;
}

export function honeyShelfLeft(profile: Profile): { id: string; honey: number; ribbon: boolean }[] {
  const left: { id: string; honey: number; ribbon: boolean }[] = [];
  if (!profile.cosmetics.ribbon) left.push({ id: 'ribbon', honey: RIBBON_HONEY, ribbon: true });
  for (const item of COSMETICS) {
    if (item.honey <= 0 || ownsCosmetic(profile, item.id)) continue;
    left.push({ id: item.id, honey: item.honey, ribbon: false });
  }
  left.sort((a, b) => a.honey - b.honey || a.id.localeCompare(b.id));
  return left;
}

/** Put an owned piece on. Outfits replace that critter's current one. */
export function wearCosmetic(profile: Profile, id: string): ShopResult {
  const item = cosmeticById(id);
  if (!item || !ownsCosmetic(profile, id)) return { ok: false, reason: 'not-for-sale' };
  equip(profile, item);
  return { ok: true };
}

function equip(profile: Profile, item: CosmeticDef): void {
  if (item.kind === 'outfit' && item.hero) profile.cosmetics.equipped.outfit[item.hero] = item.id;
  if (item.kind === 'board') profile.cosmetics.equipped.board = item.id;
  if (item.kind === 'hub' && !profile.cosmetics.equipped.hub.includes(item.id)) {
    profile.cosmetics.equipped.hub.push(item.id);
  }
}

export function buyCosmetic(profile: Profile, id: string, now: number): ShopResult {
  const item = cosmeticById(id);
  if (!item) return { ok: false, reason: 'not-for-sale' };
  if (ownsCosmetic(profile, id)) return { ok: false, reason: 'owned' };
  if (!cosmeticOpen(profile, item)) return { ok: false, reason: 'locked' };
  if (item.acorns > 0 && profile.wallet.acorns < item.acorns) return { ok: false, reason: 'acorns' };
  if (item.honey > 0 && profile.wallet.honey < item.honey) return { ok: false, reason: 'honey' };
  if (item.acorns > 0) profile.wallet.acorns -= item.acorns;
  if (item.honey > 0) profile.wallet.honey -= item.honey;
  profile.cosmetics.owned.push(id);
  equip(profile, item);
  const currencies: { acorns?: number; honey?: number } = {};
  if (item.acorns) currencies.acorns = -item.acorns;
  if (item.honey) currencies.honey = -item.honey;
  pushLedger(profile, now, 'cosmetic', currencies, 0, id);
  return { ok: true };
}

/** Repeatable hanging. Sold only after every finite acorn piece is owned. */
export function buyGarland(profile: Profile, now: number): ShopResult {
  if (cheapestAcornCosmetic(profile)) return { ok: false, reason: 'locked' };
  if (profile.wallet.acorns < GARLAND_ACORNS) return { ok: false, reason: 'acorns' };
  profile.wallet.acorns -= GARLAND_ACORNS;
  profile.cosmetics.garlands += 1;
  pushLedger(profile, now, 'cosmetic', { acorns: -GARLAND_ACORNS }, 0, `garland:${profile.cosmetics.garlands}`);
  return { ok: true };
}

/** Repeatable light. Sold only after the ribbon and every honey piece are owned. */
export function buyLantern(profile: Profile, now: number): ShopResult {
  if (honeyShelfLeft(profile).length > 0) return { ok: false, reason: 'locked' };
  if (profile.wallet.honey < LANTERN_HONEY) return { ok: false, reason: 'honey' };
  profile.wallet.honey -= LANTERN_HONEY;
  profile.cosmetics.lanterns += 1;
  pushLedger(profile, now, 'cosmetic', { honey: -LANTERN_HONEY }, 0, `lantern:${profile.cosmetics.lanterns}`);
  return { ok: true };
}

function retiredRef(ref: string | undefined): boolean {
  if (!ref) return false;
  return retiredCosmetic(ref);
}

/**
 * Drop retired glen decor from a save. Worn pieces, the ribbon, and the wallet
 * stay. Safe on a glen that never had decor, and safe to run again.
 * No acorn or honey refund.
 */
export function migrateShelfDecor(profile: Profile): boolean {
  const cosmetics = profile.cosmetics;
  let changed = false;
  const owned = cosmetics.owned.filter((id) => !retiredCosmetic(id));
  if (owned.length !== cosmetics.owned.length) {
    cosmetics.owned = owned;
    changed = true;
  }
  const hub = cosmetics.equipped.hub.filter((id) => !retiredCosmetic(id));
  if (hub.length !== cosmetics.equipped.hub.length) {
    cosmetics.equipped.hub = hub;
    changed = true;
  }
  if (cosmetics.equipped.board && retiredCosmetic(cosmetics.equipped.board)) {
    cosmetics.equipped.board = null;
    changed = true;
  }
  const outfit = cosmetics.equipped.outfit;
  for (const hero of Object.keys(outfit) as HeroId[]) {
    const id = outfit[hero];
    if (id && retiredCosmetic(id)) {
      delete outfit[hero];
      changed = true;
    }
  }
  for (const row of profile.ledger) {
    if (!retiredRef(row.ref)) continue;
    delete row.ref;
    changed = true;
  }
  return changed;
}

export function boardFile(profile: Profile): string | null {
  const id = profile.cosmetics.equipped.board;
  if (!id) return null;
  return cosmeticById(id)?.file ?? null;
}

export function outfitFile(profile: Profile, hero: HeroId): string | null {
  const id = profile.cosmetics.equipped.outfit[hero];
  if (!id) return null;
  return cosmeticById(id)?.file ?? null;
}
